import type { EventStatus, EventView } from '@cuito/types';
import { apiClient } from '../lib/apiClient';

/**
 * 社团展会（**展会存档**）的四个调用（`04-api-contract.md` §11 端点表第 1/2/5/12 行）。
 *
 * - `GET    /events`           裸数组（**不是分页包裹**，§11.8 钉死）
 * - `POST   /events`           创建社团展会（`source = TENANT`）
 * - `DELETE /events/:id`       删除（有订单 ⇒ `400 EVENT_HAS_ORDERS`）
 * - `PATCH  /events/:id/status` 状态流转（`PREPARING → ONGOING → ENDED`）
 *
 * ## 「报名记录 ≠ 展会存档」在接口层的两个方向（§11.8，**本仓最贵的一条**）
 *
 * `GET /events` **只返回 `source = TENANT` 且 `tenantId = 我` 的行**：
 *   - 平台展会（`source = PLATFORM`）**不出现**；
 *   - 报名审核通过但**未缴费**（`APPROVED`）的平台展会**也不出现** —— 它只在
 *     `GET /participations`（`api/participations.ts`）里；
 *   - 只有缴费审核通过（`PAID`）时服务端在同一事务内创建的存档才会出现在这里。
 *
 * 因此本模块**没有**任何"把报名记录合成成展会行"的入口：前端唯一的来源是这一个裸数组。
 *
 * ⚠️ `PATCH /events/:id`（编辑）不在本模块：页面没有编辑入口（冻结 testid 清单里没有它）。
 */

export type { EventView, EventStatus, EventSource, EventLocationRef } from '@cuito/types';

/** `POST /events` 的 201（§11.1）：比 `EventView` 少五个字段，**不是** `EventView` */
export interface CreatedEventView {
  id: string;
  source: string;
  tenantId: string | null;
  organizerId: string | null;
  name: string;
  status: EventStatus;
  location: { id: string; code: string; type: string };
}

/** `DELETE /events/:id` 的 200（§11.8） */
export interface DeletedEventView {
  id: string;
  deleted: boolean;
}

/** `POST /events` 的请求体（§11.1；`locationId` **不在**里面——服务端在同一事务内创建并回填） */
export interface CreateEventRequest {
  name: string;
  venue: string;
  dates: string[];
  stallNumber?: string | null;
}

/** `GET /events` 的查询参数（§11.8：`status` 可筛选） */
export interface ListEventsQuery {
  status?: EventStatus;
}

export const eventsApi = {
  /** **裸数组**（§11.8）。不存在分页包裹 —— 写成 `Paginated<EventView>` 会让页面读到 `undefined` */
  list: (params: ListEventsQuery = {}) => apiClient.get<EventView[]>('/events', { params }),

  create: (body: CreateEventRequest) => apiClient.post<CreatedEventView>('/events', body),

  /** 该场已有订单 ⇒ `400 EVENT_HAS_ORDERS`（§11.8 的守卫；错误码是本 Phase 唯一净增的一条） */
  remove: (id: string) => apiClient.del<DeletedEventView>(`/events/${id}`),

  /** 合法迁移 `PREPARING → ONGOING`、`ONGOING → ENDED`；非法迁移是 `409`（§11.7） */
  setStatus: (id: string, status: EventStatus) =>
    apiClient.patch<EventView>(`/events/${id}/status`, { status }),
};
